import { Injectable, effect, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

import { assetUrl } from './api-base';
import { ContentService } from './content.service';
import { I18nService } from './i18n/i18n.service';
import { LocalizedText, SeoPageKey, SeoSettings, SiteSettings } from './models';

export const SITE_URL = 'https://se.agency';
export const SITE_NAME = 'SE.AGENCY';
const DEFAULT_IMAGE = `${SITE_URL}/S.E_WHITE_LOGO.png`;
const DEFAULT_DESC =
  'SE.AGENCY is a creative studio crafting brand identities, smart web & app development, and marketing campaigns that turn attention into measurable impact.';

export interface SeoData {
  title?: string;
  description?: string;
  /** Path (e.g. /work) or absolute URL for canonical + og:url. */
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  /** Fixed-page key used to pull an admin-authored title/description override. */
  pageKey?: SeoPageKey;
}

/**
 * Centralised SEO: document title, meta description, canonical, Open Graph /
 * Twitter cards, keywords, verification tokens, analytics, and Organization
 * structured data. Site-wide defaults and per-page copy are loaded from the
 * admin-editable site settings; dynamic pages pass richer per-entity data.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private title = inject(Title);
  private meta = inject(Meta);
  private doc = inject(DOCUMENT);
  private i18n = inject(I18nService);
  private content = inject(ContentService);

  private seo: SeoSettings = {};
  private last: SeoData = {};

  constructor() {
    // Pull admin-editable SEO once, then re-render on every language switch.
    this.content.getSettings().subscribe((s) => this.applySettings(s));
    effect(() => {
      this.i18n.lang();
      this.render();
    });
  }

  /** Called on navigation (and by dynamic pages with richer data). */
  update(data: SeoData = {}): void {
    this.last = data;
    this.render();
  }

  private applySettings(s: SiteSettings): void {
    this.seo = s?.seo ?? {};
    this.applyGlobal();
    this.render();
  }

  /** Resolve a bilingual value for the active language. */
  private loc(t?: LocalizedText | null): string {
    if (!t) return '';
    const lang = this.i18n.lang();
    return t[lang] || t.en || t.ar || '';
  }

  private render(): void {
    const seo = this.seo;
    const siteName = seo.siteName || SITE_NAME;
    const suffix = seo.titleSuffix || siteName;
    const page = this.last.pageKey ? seo.pages?.[this.last.pageKey] : undefined;

    const titleText = this.last.title || this.loc(page?.title);
    const title = titleText ? `${titleText} — ${suffix}` : `${siteName} — Creative Studio`;
    const description =
      this.last.description ||
      this.loc(page?.description) ||
      this.loc(seo.defaultDescription) ||
      DEFAULT_DESC;
    const image = this.resolveImage(this.last.image || seo.defaultImage);
    const url = this.absolute(this.last.path);
    const type = this.last.type || 'website';
    const keywords = this.loc(seo.keywords);

    this.title.setTitle(title);
    this.setName('description', description);
    if (keywords) this.setName('keywords', keywords);
    this.setProp('og:title', title);
    this.setProp('og:description', description);
    this.setProp('og:type', type);
    this.setProp('og:url', url);
    this.setProp('og:image', image);
    this.setProp('og:site_name', siteName);
    this.setName('twitter:card', 'summary_large_image');
    this.setName('twitter:title', title);
    this.setName('twitter:description', description);
    this.setName('twitter:image', image);
    if (seo.twitterHandle) {
      this.setName('twitter:site', seo.twitterHandle);
      this.setName('twitter:creator', seo.twitterHandle);
    }
    this.setCanonical(url);
  }

  /** One-time global signals that don't depend on the current route. */
  private applyGlobal(): void {
    const seo = this.seo;
    if (seo.googleSiteVerification) {
      this.setName('google-site-verification', seo.googleSiteVerification);
    }
    this.injectAnalytics(seo.gaId);
    this.injectOrganization();
  }

  private injectAnalytics(gaId?: string): void {
    // Skip if disabled, already injected, or a gtag is already hard-coded in index.html.
    if (
      !gaId ||
      this.doc.getElementById('ga-src') ||
      this.doc.querySelector('script[src*="googletagmanager.com/gtag/js"]')
    ) {
      return;
    }
    const s1 = this.doc.createElement('script');
    s1.id = 'ga-src';
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    this.doc.head.appendChild(s1);
    const s2 = this.doc.createElement('script');
    s2.id = 'ga-init';
    s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
    this.doc.head.appendChild(s2);
  }

  private injectOrganization(): void {
    const org = this.seo.organization;
    const sameAs = (org?.sameAs ?? []).filter(Boolean);
    if (!org?.email && !org?.phone && !sameAs.length) return;
    const data: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: this.seo.siteName || SITE_NAME,
      url: SITE_URL,
      logo: this.resolveImage(this.seo.defaultImage) || DEFAULT_IMAGE,
    };
    if (org?.email) data['email'] = org.email;
    if (org?.phone) data['telephone'] = org.phone;
    if (sameAs.length) data['sameAs'] = sameAs;

    let script = this.doc.getElementById('ld-org') as HTMLScriptElement | null;
    if (!script) {
      script = this.doc.createElement('script');
      script.id = 'ld-org';
      script.type = 'application/ld+json';
      this.doc.head.appendChild(script);
    }
    script.text = JSON.stringify(data);
  }

  /** Absolute URL for an image path (handles /uploads paths and full URLs). */
  private resolveImage(path?: string): string {
    if (!path) return DEFAULT_IMAGE;
    if (/^https?:\/\//i.test(path)) return path;
    return assetUrl(path) || DEFAULT_IMAGE;
  }

  private absolute(path?: string): string {
    if (!path) {
      try {
        return this.doc.defaultView?.location.href.split('?')[0] ?? SITE_URL;
      } catch {
        return SITE_URL;
      }
    }
    return /^https?:\/\//i.test(path) ? path : SITE_URL + (path.startsWith('/') ? path : `/${path}`);
  }

  private setName(name: string, content: string): void {
    this.meta.updateTag({ name, content });
  }
  private setProp(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }
  private setCanonical(href: string): void {
    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
